import { Box, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { formatMessageBody } from './formatMessageBody';
import { MessageTimestamp } from './MessageTimestamp';
import { SenderAvatar } from './SenderAvatar';

const BODY_BACKGROUND = {
	lead: 'color-mix(in oklch, var(--rev-color-textPrimary) 10%, transparent)',
	ai: 'color-mix(in oklch, var(--rev-color-infoBold) 10%, transparent)',
} as const;

interface BubbleContentProps {
	/** Message text content. */
	body: string;
	/** ISO timestamp of when the message was created. */
	createdAt: string;
	/** Whether this is the first message in a consecutive group from the same sender. */
	isFirstInGroup: boolean;
}

interface LeadBubbleProps extends BubbleContentProps {
	/** Display name of the lead who sent the message. */
	senderName: string;
}

const LeadBubble = ({
	body,
	senderName,
	createdAt,
	isFirstInGroup,
}: LeadBubbleProps): React.ReactNode => {
	return (
		<Group itemsSpacing='6' itemsAlignX='start'>
			<Stack itemsAlignX='start' padding={{ right: 80 }} itemsSpacing='0'>
				{isFirstInGroup && (
					<Group itemsSpacing='4' itemsAlignY='center' itemsAlignX='start'>
						{isFirstInGroup && (
							<SenderAvatar senderType='lead' senderName={senderName} />
						)}
						<Text size='footnote' weight='semibold'>
							{senderName}
						</Text>
						<MessageTimestamp createdAt={createdAt} />
					</Group>
				)}
				<Box rounding='12' customBackground={BODY_BACKGROUND.lead} margin={{ left: 32 }}>
					<Text size='bodySmall' padding={{ all: 12 }}>
						{formatMessageBody(body)}
					</Text>
				</Box>
			</Stack>
		</Group>
	);
};

const AiBubble = ({ body, createdAt, isFirstInGroup }: BubbleContentProps): React.ReactNode => {
	return (
		<Group itemsSpacing='6' itemsAlignX='end'>
			<Stack itemsAlignX='end' padding={{ left: 80 }} itemsSpacing='0'>
				{isFirstInGroup && (
					<Group itemsSpacing='4' itemsAlignY='center' itemsAlignX='end'>
						<MessageTimestamp createdAt={createdAt} />
						<Text size='footnote' weight='semibold'>
							AI BDC
						</Text>
						{isFirstInGroup && (
							<SenderAvatar senderType='ai_agent' senderName='AI BDC' />
						)}
					</Group>
				)}
				<Box rounding='12' customBackground={BODY_BACKGROUND.ai} margin={{ right: 32 }}>
					<Text size='bodySmall' padding={{ all: 12 }}>
						{formatMessageBody(body)}
					</Text>
				</Box>
			</Stack>
		</Group>
	);
};

export interface MessageBubbleProps {
	/** Message text content. */
	body: string;
	/** Type of entity that sent this message. */
	senderType: 'lead' | 'ai_agent' | 'system';
	/** Display name of the message sender. */
	senderName: string;
	/** ISO timestamp of when the message was created. */
	createdAt: string;
	/** Whether this is the first message in a consecutive group from the same sender. */
	isFirstInGroup: boolean;
	/** Whether this is the last message in a consecutive group from the same sender. */
	isLastInGroup: boolean;
}

export const MessageBubble = ({
	body,
	senderType,
	senderName,
	createdAt,
	isFirstInGroup,
}: MessageBubbleProps): React.ReactNode => {
	if (senderType === 'lead') {
		return (
			<LeadBubble
				body={body}
				senderName={senderName}
				createdAt={createdAt}
				isFirstInGroup={isFirstInGroup}
			/>
		);
	}

	return <AiBubble body={body} createdAt={createdAt} isFirstInGroup={isFirstInGroup} />;
};
